import {
  Avatar,
  Dropdown,
  Icon,
  IconButton,
  Menu,
  MenuItem,
  Search,
  Spacer,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tag,
  Text,
  TextBody,
  TextCaption,
  TextSubtitle,
  TextTitle,
  Tooltip,
} from '@pypestream/design-system';
import {
  TranslationComponent,
  dateFnsLocales,
  fallbackLanguage,
  useTranslation,
} from '@pypestream/translations';
import { formatTimeWithTimezone } from '@pypestream/utils';
import { FC, useEffect, useState } from 'react';
import { useUserTimeZone } from '../../../hooks';
import { normalizeHasuraTimezone } from '../../../utils';
import { textFilter } from '../../../utils/text-filter';
import {
  sendManagerEvent,
  useManagerCtxSelector,
} from '../../../xstate/app.xstate';
import {
  NEW_TEAM_TEMP_ID,
  TeamDetailsType,
  TeamUsersType,
} from '../../../xstate/team-details-xstate-helper';
import './team-users.scss';

export const TeamUsersTable: FC<{
  teamUsers:
    | TeamDetailsType['usersFromCurrentOrg']
    | TeamDetailsType['usersFromOtherOrgs'];
  availableUsers?: TeamUsersType;
  teamId?: string;
  readOnly?: boolean;
}> = ({ teamUsers, availableUsers, teamId, readOnly }) => {
  const { defaultLanguage } = useManagerCtxSelector((ctx) => ({
    defaultLanguage: ctx.userInfo?.defaultLanguage,
  }));
  const userTimeZone = useUserTimeZone();
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [filteredUsers, setFilteredUsers] = useState(availableUsers);
  const [t] = useTranslation();
  const isNoUsersInOrg = !availableUsers?.length && !teamUsers?.length;

  function toggleSelectedUser(userId: string) {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  }

  function addSelectedUsers() {
    setSearchFilter('');
    if (!selectedUsers.length) return;

    sendManagerEvent({
      type: 'manager.teamDetails.addUsersToTeam',
      teamId,
      userIds: selectedUsers,
    });
    setSelectedUsers([]);
  }

  function removeSelectedUser(userId: string) {
    sendManagerEvent({
      type: 'manager.teamDetails.removeUserFromTeam',
      teamId,
      userId,
    });
  }

  function renderAddUsersButton() {
    return (
      <>
        <Icon
          color="purple"
          variant="filled"
          name="plus"
          size="medium"
          style={{ marginBlock: '-8px' }}
        />
        <Text size="2xsmall" variant="secondary">
          <TranslationComponent i18nKey="manager/teams:teamDetails.teamUsers.addUsers">
            Add Users
          </TranslationComponent>
        </Text>
      </>
    );
  }

  useEffect(() => {
    if (searchFilter) {
      setFilteredUsers(
        availableUsers?.filter((availableUser) =>
          textFilter(searchFilter, [
            `${availableUser.firstName} ${availableUser.lastName}`,
            availableUser.userName,
          ])
        )
      );
    } else {
      setFilteredUsers(availableUsers);
    }
  }, [searchFilter, availableUsers]);

  if (!teamUsers.length && readOnly) {
    return (
      <Text
        size="2xsmall"
        variant="secondary"
        i18nKey="manager/teams:teamDetails.teamUsers.noUsersInTeam"
      >
        There are no users in this team.
      </Text>
    );
  }

  return (
    <Table size="small">
      {!!teamUsers.length && (
        <TableHead>
          <TableRow>
            <TableCell variant="th">
              <TranslationComponent i18nKey="manager/teams:teamDetails.teamUsers.user">
                User
              </TranslationComponent>
              <IconButton size="xsmall" name="chevron-down" slot="sorter" />
            </TableCell>
            <TableCell variant="th">
              <TranslationComponent i18nKey="manager/teams:teamDetails.teamUsers.lastActive">
                Last Active
              </TranslationComponent>
              <IconButton size="xsmall" name="chevron-down" slot="sorter" />
            </TableCell>
          </TableRow>
        </TableHead>
      )}
      <TableBody>
        {teamUsers.map((teamUser) => (
          <TableRow key={teamUser.user.id}>
            <TableCell>
              <Stack alignItems="center" nowrap>
                <Avatar
                  label={teamUser.user.firstName}
                  src={teamUser.user.picture}
                ></Avatar>
                <Stack direction="column" gutter="none">
                  <TextSubtitle size="small" font-weight="medium">
                    {teamUser.user.firstName} {teamUser.user.lastName}
                  </TextSubtitle>
                  <Text size="2xsmall" variant="secondary">
                    {teamUser.user.userName}
                  </Text>
                </Stack>
                {teamUser.user.status === 'INVITED' ? (
                  <Tag minimal new-variant="filled">
                    Pending invite
                  </Tag>
                ) : null}
              </Stack>
            </TableCell>
            <TableCell>
              <TextBody variant="secondary">
                {formatTimeWithTimezone({
                  fromDate: normalizeHasuraTimezone(teamUser.user.lastActiveAt),
                  locale: dateFnsLocales[defaultLanguage || fallbackLanguage],
                  userTimeZone,
                })}
              </TextBody>
            </TableCell>
            {!readOnly && (
              <TableCell align="end">
                <IconButton
                  name="close"
                  onClick={() => removeSelectedUser(teamUser.user.id)}
                  style={{ marginLeft: 'auto' }}
                ></IconButton>
              </TableCell>
            )}
          </TableRow>
        ))}
        {!readOnly && (
          <TableRow id="dropdown-trigger1" actionable={!isNoUsersInOrg}>
            <TableCell colspan={3}>
              <Stack alignItems="center" nowrap>
                {isNoUsersInOrg ? (
                  <Tooltip
                    content={
                      t('manager/teams:teamDetails.teamUsers.noUsers', {
                        defaultValue: 'There are no users in this Org',
                      }) || ''
                    }
                  >
                    {renderAddUsersButton()}
                  </Tooltip>
                ) : (
                  renderAddUsersButton()
                )}
              </Stack>
              <Dropdown
                id="dropdown1"
                trigger={isNoUsersInOrg ? '' : 'dropdown-trigger1'}
                placement="bottom-start"
                stayOpenOnSelect
                onclose={addSelectedUsers}
                flip={false}
              >
                <div className="c-team-users-dropdown-search">
                  <Search
                    size="medium"
                    slot="dropdown-top"
                    onInput={(e) => setSearchFilter(e.target.value)}
                    value={searchFilter}
                  />
                  {!!searchFilter && !filteredUsers?.length && (
                    <>
                      <Spacer size="medium" />
                      <Text size="2xsmall" variant="secondary">
                        No results found
                      </Text>
                    </>
                  )}
                </div>
                <Menu>
                  {filteredUsers?.map((user) => (
                    <MenuItem
                      key={user.id}
                      onClick={() => toggleSelectedUser(user.id)}
                      selected={selectedUsers.includes(user.id)}
                    >
                      <Stack alignItems="center" nowrap>
                        <Avatar
                          label={user.firstName}
                          src={user.picture}
                        ></Avatar>
                        <Stack direction="column" gutter="none">
                          <TextSubtitle size="small" font-weight="medium">
                            {user.firstName} {user.lastName}
                          </TextSubtitle>
                          <Text size="2xsmall" variant="secondary">
                            {user.userName}
                          </Text>
                        </Stack>
                      </Stack>
                    </MenuItem>
                  ))}
                </Menu>
              </Dropdown>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export const TeamUsers: FC<{
  teamUsersFromCurrentOrg: TeamDetailsType['usersFromCurrentOrg'];
  teamUsersFromOtherOrgs: TeamDetailsType['usersFromOtherOrgs'];
  availableUsers: TeamUsersType;
  teamId: string;
  canManageAccess: boolean;
}> = ({
  teamUsersFromCurrentOrg,
  teamUsersFromOtherOrgs,
  availableUsers,
  teamId,
  canManageAccess,
}) => {
  return (
    <>
      <TextTitle
        test-id="team-users"
        size="xsmall"
        i18nKey="manager/teams:teamDetails.teamUsers.teamMembers"
      >
        Team Members
      </TextTitle>
      <TextCaption i18nKey="manager/teams:teamDetails.teamUsers.teamMembersSubTitle">
        Team members inherit all roles assigned to the Team.
      </TextCaption>
      <Spacer size="large" />
      <TeamUsersTable
        teamUsers={teamUsersFromCurrentOrg}
        availableUsers={availableUsers}
        teamId={teamId}
        readOnly={!canManageAccess}
      />
      {teamId !== NEW_TEAM_TEMP_ID && !!teamUsersFromOtherOrgs.length && (
        <>
          <Spacer size="large" />
          <TextTitle
            test-id="team-users"
            size="xsmall"
            i18nKey="manager/teams:teamDetails.teamUsers.teamMembersOtherOrgs"
          >
            Team Members in Other Orgs
          </TextTitle>
          <TextCaption i18nKey="manager/teams:teamDetails.teamUsers.teamMembersOtherOrgsSubTitle">
            Team members in other Orgs cannot be managed from this screen. To
            remove them from this Team, go to their user settings.
          </TextCaption>
          <Spacer size="large" />
          <TeamUsersTable teamUsers={teamUsersFromOtherOrgs} readOnly />
        </>
      )}
    </>
  );
};
